import { JobFileChain } from '@core/models/job-file/job-file-chain.model';
import { WorkOrder } from '../../control-tower/models/work-order/work-order';

export interface JobSummaryBaseItem {
  jobName: string;
  jobReferenceId: string;
  loadReferenceId: string;
  createdDateTime: Date;
  jpmId?: string;
  allWorkOrders?: WorkOrder[];
  completionPercentage?: number;
  expectedEndTime?: string;
  urlVersion?: string;
  jobFileChain?: Map<number, JobFileChain> | null;
  jobType?: string;
  // Fields shared by every stage
  customerName?: string | null;
  status?: string | null;
}

/** Stages that list suppliers (all orgs an RFQ went to, or the accepting supplier). */
export interface SupplierFields {
  suppliers?: string[] | null;
}

/** Planned figures shown in Load Assignment and Contracted. */
export interface EstimateFields {
  estimatedDistance?: number | null;
  estimatedDurationMinutes?: number | null;
  locationsCount?: number | null;
}

// Completed job item
export interface CompletedJobItem extends JobSummaryBaseItem, SupplierFields {
  vehicleIds: string;
  distance: number;
  duration: string;
  driver?: string | null;
  /** Actual duration in minutes; `duration` text is the fallback. */
  durationMinutes?: number | null;
}

// Delivery in progress job item
export interface DeliveryInProgressJobItem extends JobSummaryBaseItem, SupplierFields {
  vehicleId: string;
  completionPercentage: number;
  expectedEndTime: string;
  driver?: string | null;
}

// Notified job item
export interface NotifiedJobItem extends JobSummaryBaseItem, SupplierFields {
  driver: string;
  vehicle: string;
  expectedStartTime: Date;
}

// Contracted job item
export interface ContractedJobItem extends JobSummaryBaseItem, EstimateFields {
  driverName: string;
  vehicle: string;
  customerContractId?: string | null;
}

// Load Assignment job item
export interface LoadAssignmentJobItem extends JobSummaryBaseItem, EstimateFields {}

// Pending Acceptance job item
export interface PendingAcceptanceJobItem extends JobSummaryBaseItem, SupplierFields {
  customerContractId?: string | null;
  /** SERVICE_REQUEST or RFQ */
  requestType?: string | null;
}

// Accepted job item
export interface AcceptedJobItem extends JobSummaryBaseItem, SupplierFields {
  customerContractId?: string | null;
  costContractId?: string | null;
}

// Terminated job item
export interface TerminatedJobItem extends JobSummaryBaseItem, SupplierFields {
  driver?: string | null;
  vehicle?: string | null;
  terminatedBy?: string | null;
  terminatedAt?: Date | null;
}

// Planned job item
export interface PlannedJobItem extends JobSummaryBaseItem {
  vehicleId?: string;
  distance?: number;
  duration?: string;
}

// Pagination metadata
export interface PaginationMeta {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  totalPages: number;
}

// Complete response object
export interface JobSummaryResponse {
  completedJobs?: CompletedJobItem[];
  deliveryInProgressJobs?: DeliveryInProgressJobItem[];
  notifiedJobs?: NotifiedJobItem[];
  contractedJobs?: ContractedJobItem[];
  plannedJobs?: PlannedJobItem[];
  loadAssignmentJobs?: LoadAssignmentJobItem[];
  pendingAcceptanceJobs?: PendingAcceptanceJobItem[];
  acceptedJobs?: AcceptedJobItem[];
  terminatedJobs?: TerminatedJobItem[];
  pagination: PaginationMeta;
  /** Tenant (org) IANA timezone for dates; browser zone when missing. */
  timeZone?: string | null;
  error?: string | null;
}

export interface JobSummaryRequest {
  orgId: string;
  operationId: string;
  timeRange: string;
  status?: JobStatusEnum;
  startDate?: Date;
  endDate?: Date;
  pageIndex: number;
  pageSize: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export type JobSummaryItem = CompletedJobItem | DeliveryInProgressJobItem | NotifiedJobItem | ContractedJobItem | PlannedJobItem
  | LoadAssignmentJobItem | PendingAcceptanceJobItem | AcceptedJobItem | TerminatedJobItem;

export enum TimeRangeEnum {
  hours24 = '24Hours',
  days7 = '7Days',
  days30 = '30Days',
  months12 = '12Months',
  custom = 'Custom'
}

// Codes are part of the backend contract. Some tabs use a different UI label
// (Planned -> "Planning", DeliveryInProgress -> "In Progress"); see JOB_SUMMARY_TABS.
export enum JobStatusEnum {
  completed = 'Completed',
  deliveryInProgress = 'DeliveryInProgress',
  notified = 'Notified',
  contracted = 'Contracted',
  planned = 'Planned',
  loadAssignment = 'LoadAssignment',
  pendingAcceptance = 'PendingAcceptance',
  accepted = 'Accepted',
  terminated = 'Terminated'
}

/** Job Summary tabs, left to right. */
export const JOB_SUMMARY_TABS: { status: JobStatusEnum; label: string }[] = [
  { status: JobStatusEnum.planned, label: 'Planning' },
  { status: JobStatusEnum.loadAssignment, label: 'Load Assignment' },
  { status: JobStatusEnum.contracted, label: 'Contracted' },
  { status: JobStatusEnum.pendingAcceptance, label: 'Pending Acceptance' },
  { status: JobStatusEnum.accepted, label: 'Accepted' },
  { status: JobStatusEnum.notified, label: 'Notified' },
  { status: JobStatusEnum.deliveryInProgress, label: 'In Progress' },
  { status: JobStatusEnum.completed, label: 'Completed' },
  { status: JobStatusEnum.terminated, label: 'Terminated' }
];

/**
 * Default sort per tab; `stageDate` is when the load entered the stage.
 * Notified / In Progress / Completed: their natural sort columns (Expected Start Time, Completion %,
 * Duration) are not sortable, so they open on created date, newest first.
 */
export const JOB_SUMMARY_DEFAULT_SORT: Record<JobStatusEnum, { column: string; direction: 'asc' | 'desc' }> = {
  [JobStatusEnum.planned]: { column: 'stageDate', direction: 'desc' },
  [JobStatusEnum.loadAssignment]: { column: 'stageDate', direction: 'desc' },
  [JobStatusEnum.contracted]: { column: 'customerContractId', direction: 'asc' },
  [JobStatusEnum.pendingAcceptance]: { column: 'stageDate', direction: 'desc' },
  [JobStatusEnum.accepted]: { column: 'stageDate', direction: 'desc' },
  [JobStatusEnum.notified]: { column: 'createdDateTime', direction: 'desc' },
  [JobStatusEnum.deliveryInProgress]: { column: 'createdDateTime', direction: 'desc' },
  [JobStatusEnum.completed]: { column: 'createdDateTime', direction: 'desc' },
  // The stage's last column, descending.
  [JobStatusEnum.terminated]: { column: 'terminatedAt', direction: 'desc' }
};

export function isJobStatus(value: unknown): value is JobStatusEnum {
  return Object.values(JobStatusEnum).includes(value as JobStatusEnum);
}

export interface JobSummaryTimeRange {
  startDate: string;
  endDate: string;
}
